/* eslint-disable class-methods-use-this */
import crypto from 'crypto';
import { ApolloClient } from 'apollo-client';
import { createHttpLink } from 'apollo-link-http';
import { createUploadLink } from 'apollo-upload-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { setContext } from 'apollo-link-context';
import { ApolloLink } from 'apollo-link';
import { onError } from 'apollo-link-error';
import { deriveApiUrlFromEnv } from './utils';
import EventSystem from './EventSystem';
// import { log } from '#emyw/core-lib';
import log from './logger';
const {
  REACT_APP_SHOW_BASE_URL,
  REACT_APP_SHOW_API_PATH,
  REACT_APP_TSDB_BASE_URL,
  REACT_APP_TSDB_API_PATH,
  REACT_APP_SHOWDB_API_KEY,
  REACT_APP_AUTH_BASE_URL,
  REACT_APP_AUTH_API_PATH,
  REACT_APP_ACCOUNT_BASE_URL,
  REACT_APP_ACCOUNT_API_PATH,
  REACT_APP_ENJOY_EXTENSION_ID
} = process.env;
const {
  REACT_APP_TOKEN_NAME,
  REACT_APP_REFRESH_TOKEN_NAME
} = process.env;
const getTokens = () => {
  // on 6/3/2020 we changed the name of the refresh token from enjoy-refreshToken to enjoy-refresh-token
  // to make this change as seamless as possible, we'll check for that old token name, transfer it's value if
  // the new refresh token isn't already there, and then remove it.
  if (['enjoy-token', 'enjoy-token-staging'].includes(REACT_APP_TOKEN_NAME)) {
    const newRefreshToken = localStorage.getItem(REACT_APP_REFRESH_TOKEN_NAME);
    if ((!newRefreshToken || newRefreshToken === 'null') && localStorage.getItem('enjoy-refreshToken')) {
      localStorage.setItem(REACT_APP_REFRESH_TOKEN_NAME, localStorage.getItem('enjoy-refreshToken'));
    }
    if (localStorage.getItem('enjoy-refreshToken')) localStorage.removeItem('enjoy-refreshToken');
  }
  return {
    [REACT_APP_TOKEN_NAME]: localStorage.getItem(REACT_APP_TOKEN_NAME),
    [REACT_APP_REFRESH_TOKEN_NAME]: localStorage.getItem(REACT_APP_REFRESH_TOKEN_NAME),
    'api-key': REACT_APP_SHOWDB_API_KEY
  };
};
const clearTokens = () => {
  localStorage.removeItem(REACT_APP_TOKEN_NAME);
  localStorage.removeItem(REACT_APP_REFRESH_TOKEN_NAME);
  if (REACT_APP_ENJOY_EXTENSION_ID) {
    try {
      window.chrome.runtime.sendMessage(REACT_APP_ENJOY_EXTENSION_ID, {
        logOut: true
      });
    } catch (error) {
      // absorb failure to send message to extension
      log.trace(error);
    }
  }
};
const globalErrorLink = onError(_ref => {
  let {
    graphQLErrors,
    networkError
  } = _ref;
  let errorMessage = networkError;
  if (graphQLErrors) {
    errorMessage = graphQLErrors.map(_ref2 => {
      let {
        message,
        locations,
        path
      } = _ref2;
      return "[GraphQL error]: Message: ".concat(message, ", Location: ").concat(locations, ", Path: ").concat(path);
    });
  }
  if (errorMessage) {
    log.trace('enjoyClients Error response:', networkError, graphQLErrors);
    // there was a case where the extension was setting tokens to the string 'undefined'
    // and would cause the site to simply hang an loading.
    // might want to check network response codes when deciding to logout as well.
    // Originally, we logged out after 'any' error, but it seemed to cause people to get
    // kicked out too frequently... maybe due to network drops or perhaps pod restarts... not sure
    // Now we just clearTokens in certain cases.
    if ((localStorage.getItem(REACT_APP_TOKEN_NAME) || '').length < 15 || (localStorage.getItem(REACT_APP_REFRESH_TOKEN_NAME) || '').length < 15) clearTokens();
    EventSystem.publish('uhoh', {
      errorMessage
    });
    // sessionStorage.setItem('uhoh-networkError', networkError);
    // sessionStorage.setItem('uhoh-graphQLErrors', graphQLErrors);
    // if (window.location.pathname.indexOf('/uhoh') !== 0
    //     && window.location.search.indexOf('%2Fuhoh') === -1
    //     && window.location.protocol !== 'chrome-extension:') window.location = '/uhoh';
    // console.log(`[Network error]: ${networkError}`, window.location);
  }
});
const checkMissingClientEnvVars = clientName => {
  let baseUrlVarName;
  let apiPathVarName;
  let apiKeyVarName;
  if (clientName === 'ShowClient') {
    baseUrlVarName = 'REACT_APP_SHOW_BASE_URL';
    apiPathVarName = 'REACT_APP_SHOW_API_PATH';
  } else if (clientName === 'AuthClient') {
    baseUrlVarName = 'REACT_APP_AUTH_BASE_URL';
    apiPathVarName = 'REACT_APP_AUTH_API_PATH';
  } else if (clientName === 'AccountClient') {
    baseUrlVarName = 'REACT_APP_ACCOUNT_BASE_URL';
    apiPathVarName = 'REACT_APP_ACCOUNT_API_PATH';
  } else if (clientName === 'TSDBClient') {
    baseUrlVarName = 'REACT_APP_TSDB_BASE_URL';
    apiPathVarName = 'REACT_APP_TSDB_API_PATH';
    apiKeyVarName = 'REACT_APP_SHOWDB_API_KEY';
  } else {
    log.warn('Unknown client', clientName);
    return;
  }
  const baseUrl = process.env[baseUrlVarName];
  const apiPath = process.env[apiPathVarName];
  const apiKey = apiKeyVarName ? process.env[apiKeyVarName] : 'dummyKey';
  if (baseUrl && apiPath && apiKey) return;
  const missingEnvVars = [];
  if (!baseUrl) missingEnvVars.push('base url' /* baseUrlVarName */);
  if (!apiPath) missingEnvVars.push('api path' /* apiPathVarName */);
  if (!apiKey) missingEnvVars.push('api key' /* apiKeyVarName */);
  log.error("Cannot access ".concat(clientName, ": ").concat(missingEnvVars.join(' and '), " env var").concat(missingEnvVars.length > 1 ? 's' : '', " undefined"));
};
const showHttpLink = createHttpLink({
  uri: deriveApiUrlFromEnv(REACT_APP_SHOW_BASE_URL + REACT_APP_SHOW_API_PATH),
  credentials: 'include'
});
const tsdbHttpLink = createHttpLink({
  uri: deriveApiUrlFromEnv(REACT_APP_TSDB_BASE_URL + REACT_APP_TSDB_API_PATH)
});
const authHttpLink = createHttpLink({
  uri: deriveApiUrlFromEnv(REACT_APP_AUTH_BASE_URL + REACT_APP_AUTH_API_PATH),
  credentials: 'include'
});
const accountHttpLink = createHttpLink({
  uri: deriveApiUrlFromEnv(REACT_APP_ACCOUNT_BASE_URL + REACT_APP_ACCOUNT_API_PATH),
  credentials: 'include'
});
const accountUploadLink = createUploadLink({
  uri: deriveApiUrlFromEnv(REACT_APP_ACCOUNT_BASE_URL + REACT_APP_ACCOUNT_API_PATH),
  credentials: 'include'
});
export const authMiddlewareLink = setContext(() => ({
  /* Get the authentication token from local storage if it exists */
  headers: getTokens()
}));
const authAfterwareLink = new ApolloLink((operation, forward) => forward(operation).map(response => {
  const {
    response: {
      headers
    }
  } = operation.getContext();
  // log.trace('headers received:', headers);
  if (headers) {
    const token = headers.get(REACT_APP_TOKEN_NAME);
    const refreshToken = headers.get(REACT_APP_REFRESH_TOKEN_NAME);
    // log.trace(`setting tokens: ${token}`);
    if (token) {
      localStorage.setItem(REACT_APP_TOKEN_NAME, token);
    }
    if (refreshToken) {
      localStorage.setItem(REACT_APP_REFRESH_TOKEN_NAME, refreshToken);
    }
  }
  return response;
}));

/* Use with apollo-client */
const showLink = ApolloLink.from([authAfterwareLink, authMiddlewareLink, globalErrorLink, showHttpLink]);
const authLink = ApolloLink.from([authAfterwareLink, authMiddlewareLink, globalErrorLink, authHttpLink]);
const accountLink = ApolloLink.from([authAfterwareLink, authMiddlewareLink, globalErrorLink, accountHttpLink]);
export const tsdbMiddlewareLink = setContext(() => ({
  headers: {
    ...getTokens(),
    'api-key': REACT_APP_SHOWDB_API_KEY || ''
  }
}));
const tsdbLink = ApolloLink.from([authAfterwareLink, tsdbMiddlewareLink, tsdbHttpLink]);

// const showLink = authAfterwareLink.concat(authMiddlewareLink.concat(showHttpLink));
// const authLink = authAfterwareLink.concat(authMiddlewareLink.concat(authHttpLink));
// const accountLink = authAfterwareLink.concat(authMiddlewareLink.concat(accountHttpLink));

const nonExtensionMode = (process.env.REACT_APP_CHROME_EXTENSION_MODE || 'false').toLowerCase() !== 'true';
const proxyGraphqlQuery = (client, queryParams) => new Promise((resolve, reject) => {
  // eslint-disable-next-line no-undef
  chrome.runtime.sendMessage({
    action: 'enjoyQuery',
    tokens: getTokens(),
    client,
    queryParams
  }, res => {
    // log.trace('got message response from background', res);
    if (res === null) {
      reject(new Error('Proxied response null'));
    } else {
      resolve(res);
    }
  });
});
const proxyGraphqlMutate = (client, mutateParams) => new Promise((resolve, reject) => {
  // eslint-disable-next-line no-undef
  chrome.runtime.sendMessage({
    action: 'enjoyMutate',
    tokens: getTokens(),
    client,
    mutateParams
  }, res => {
    // log.trace('got message response from background mutate', res);
    if (res === null) {
      reject(new Error('Proxied mutate response null'));
    } else {
      resolve(res);
    }
  });
});
export const ShowClientCore = new ApolloClient({
  link: showLink,
  cache: new InMemoryCache({
    addTypename: false
  })
});
export const TSDBClientCore = new ApolloClient({
  link: tsdbLink,
  cache: new InMemoryCache({
    addTypename: false
  })
});
export const AuthClientCore = new ApolloClient({
  link: authLink,
  cache: new InMemoryCache()
});
export const AccountClientCore = new ApolloClient({
  link: accountLink,
  cache: new InMemoryCache()
});
export const AccountClientUploadCore = new ApolloClient({
  link: accountUploadLink,
  cache: new InMemoryCache()
});
const storeResultsInCache = _ref3 => {
  let {
    key,
    res,
    variables
  } = _ref3;
  // Consider pushing the cache key down a few levels into the object so if the client
  // app wants to clear it, they can.
  // eslint-disable-next-line no-return-assign, no-param-reassign
  // Object.keys(res.data).forEach((prop) => {
  //     res.data[prop].memoryCacheKey = key;
  //     Object.keys(res.data[prop]).forEach((prop2) => {
  //         if (typeof res.data[prop][prop2] === 'object' && res.data[prop][prop2] !== null) {
  //             res.data[prop][prop2].memoryCacheKey = key;
  //             Object.keys(res.data[prop][prop2]).forEach((prop3) => {
  //                 if (typeof res.data[prop][prop2][prop3] === 'object' && res.data[prop][prop2][prop3] !== null) {
  //                     res.data[prop][prop2][prop3].memoryCacheKey = key;
  //                 }
  //             });
  //         }
  //     });
  // });
  // log.trace('caching results');
  if (!(variables || {}).cacheOnly && !(variables || {}).cacheResults) {
    log.trace('no caching param');
    return;
  }
  const expiresAt = new Date();
  expiresAt.setHours(expiresAt.getMinutes() + (variables.cacheResults.expireAfterMinutes || 60));
  window.enjoyGqlCache[key] = {
    data: res,
    expiresAt
  };
};
export class ShowClientWrapper {
  async query(params) {
    checkMissingClientEnvVars('ShowClient');
    if (nonExtensionMode) {
      // console.log('here i am', window.enjoy);
      if (!window.enjoyGqlCache) window.enjoyGqlCache = {};
      const {
        query,
        variables
      } = params;
      const {
        body
      } = query.loc.source;
      // remove cache related variables for generating the key
      const keyVars = {
        ...(variables || {})
      };
      delete keyVars.cacheOnly;
      delete keyVars.refreshCache;
      delete keyVars.cacheResults;
      const key = crypto.createHmac('sha256', '$ome$alt').update("".concat(body, "-").concat(JSON.stringify(keyVars))).digest('hex');

      // const t0 = performance.now();
      // ShowClientCore.query(params).then(res => console.log('this is it', res)); // use this if you don't want performance timing
      // log.trace('going traditional');
      return new Promise(resolve => {
        ShowClientCore.query(params).then(res => {
          // const t1 = performance.now();
          // log.trace(`Webview Retrieval took ${t1 - t0} ms    --> ${body.substr(0, 100).replace(/\n/, '').trim()}`, variables);
          resolve(res);
          storeResultsInCache({
            key,
            res,
            variables
          });
        });
      });
    }
    // otherwise proxy the call through the background script
    // log.trace(nonExtensionMode, 'ShowClient', 'query', params);
    return proxyGraphqlQuery('ShowClient', params);
  }
  mutate(params) {
    checkMissingClientEnvVars('ShowClient');
    if (nonExtensionMode) return ShowClientCore.mutate(params);
    // log.trace(nonExtensionMode, 'ShowClient', 'mutate', params);
    return proxyGraphqlMutate('ShowClient', params);
  }
}
export class AuthClientWrapper {
  query(params) {
    checkMissingClientEnvVars('AuthClient');
    if (nonExtensionMode) return AuthClientCore.query(params);
    // otherwise proxy the call through the background script
    // log.trace(nonExtensionMode, 'AuthClient', 'query', params);
    return proxyGraphqlQuery('AuthClient', params);
  }
  mutate(params) {
    checkMissingClientEnvVars('AuthClient');
    if (nonExtensionMode) return AuthClientCore.mutate(params);
    // log.trace(nonExtensionMode, 'AuthClient', 'mutate', params);
    return proxyGraphqlMutate('AuthClient', params);
  }
}
export class AccountClientWrapper {
  query(params) {
    checkMissingClientEnvVars('AccountClient');
    if (nonExtensionMode) return AccountClientCore.query(params);
    // otherwise proxy the call through the background script
    // log.trace(nonExtensionMode, 'AccountClient', 'query', params);
    return proxyGraphqlQuery('AccountClient', params);
  }
  mutate(params) {
    checkMissingClientEnvVars('AccountClient');
    if (nonExtensionMode) return AccountClientCore.mutate(params);
    // log.trace(nonExtensionMode, 'AccountClient', 'mutate', params);
    return proxyGraphqlMutate('AccountClient', params);
  }
}
export class TSDBClientWrapper {
  query(params) {
    checkMissingClientEnvVars('TSDBClient');
    if (nonExtensionMode) return TSDBClientCore.query(params);
    // otherwise proxy the call through the background script
    // log.trace(nonExtensionMode, 'AccountClient', 'query', params);
    return proxyGraphqlQuery('TSDBClient', params);
  }
  mutate(params) {
    checkMissingClientEnvVars('TSDBClient');
    if (nonExtensionMode) return TSDBClientCore.mutate(params);
    // log.trace(nonExtensionMode, 'AccountClient', 'mutate', params);
    return proxyGraphqlMutate('TSDBClient', params);
  }
}
export const ShowClient = new ShowClientWrapper();
export const TSDBClient = new TSDBClientWrapper();
export const AuthClient = new AuthClientWrapper();
export const AccountClient = new AccountClientWrapper();